import React, { useState, useEffect } from 'react';
import { Shield, Globe, AlertTriangle, Hash, Link2, RefreshCw } from 'lucide-react';
import api from '../api';

interface Pulse {
  id: string;
  name: string;
  description?: string;
  tags: string[];
  indicators: number;
  adversary: string;
}

interface ThreatIntelData {
  source: string;
  timestamp: string;
  campaigns: number;
  pulses: Pulse[];
  stats: {
    total_indicators: number;
    malware_families: number;
    threat_actors: number;
  };
  demo?: boolean;
  cached?: boolean;
}

const ThreatIntelPanel: React.FC = () => {
  const [threatData, setThreatData] = useState<ThreatIntelData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchThreatIntel = async () => {
    setLoading(true);
    try {
      const response = await api.getThreatIntel();
      // Backend returns data directly, not wrapped in success/data
      if (response && (response.source || response.campaigns)) {
        setThreatData(response);
        setError(null);
      } else if (response.error) {
        setError(response.error);
      } else {
        setError('Invalid response from threat intel API');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch threat intel');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThreatIntel();
    const interval = setInterval(fetchThreatIntel, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading && !threatData) {
    return (
      <div className="bg-arbitium-dark/50 backdrop-blur-sm rounded-xl p-6 border border-arbitium-primary/20">
        <div className="animate-pulse flex items-center gap-3">
          <Shield className="w-6 h-6 text-arbitium-accent" />
          <span className="text-gray-400">Loading threat intelligence...</span>
        </div>
      </div>
    );
  }

  if (error && !threatData) {
    return (
      <div className="bg-arbitium-dark/50 backdrop-blur-sm rounded-xl p-6 border border-red-500/30">
        <div className="flex items-center gap-3 text-red-400">
          <AlertTriangle className="w-6 h-6" />
          <span>Failed to load threat intel: {error}</span>
        </div>
      </div>
    );
  }

  if (!threatData) return null;

  const { source, campaigns, pulses, stats, timestamp, demo, cached } = threatData;

  return (
    <div className="bg-arbitium-dark/50 backdrop-blur-sm rounded-xl p-6 border border-arbitium-primary/20">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-arbitium-accent/20 rounded-lg">
            <Shield className="w-6 h-6 text-arbitium-accent" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Threat Intelligence</h3>
            <p className="text-sm text-gray-400">
              Powered by {source || 'AlienVault OTX'}
              {demo && <span className="ml-2 text-yellow-400">(Demo)</span>}
              {cached && <span className="ml-2 text-blue-400">(Cached)</span>}
            </p>
          </div>
        </div>
        <button
          onClick={fetchThreatIntel}
          className="p-2 hover:bg-arbitium-primary/10 rounded-lg transition-colors"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="bg-gradient-to-r from-arbitium-primary/10 to-arbitium-accent/10 rounded-lg p-4 mb-6 border border-arbitium-primary/20">
        <div className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-arbitium-accent" />
          <span className="text-2xl font-bold text-white">{(campaigns || 0).toLocaleString()}</span>
          <span className="text-gray-400">Active Threat Campaigns Monitored</span>
        </div>
      </div>

      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-400 mb-3">Notable Campaigns (24h)</h4>
        <div className="flex flex-wrap gap-2">
          {(pulses || []).slice(0, 5).map((pulse) => (
            <div
              key={pulse.id}
              className="px-3 py-2 bg-arbitium-darker/50 rounded-lg border border-arbitium-primary/20 hover:border-arbitium-accent/50 transition-colors cursor-pointer group"
              title={pulse.description || pulse.name}
            >
              <div className="text-sm font-medium text-white truncate max-w-[140px] group-hover:text-arbitium-accent transition-colors">
                {pulse.name}
              </div>
              <div className="text-xs text-gray-500">
                {pulse.indicators || 0} IOCs
                {pulse.adversary && pulse.adversary !== 'Unknown' && (
                  <span className="ml-1 text-red-400">• {pulse.adversary}</span>
                )}
              </div>
            </div>
          ))}
          {(pulses || []).length > 5 && (
            <div className="px-3 py-2 bg-arbitium-darker/30 rounded-lg border border-arbitium-primary/10">
              <div className="text-sm text-gray-500">
                +{pulses.length - 5} more
              </div>
            </div>
          )}
        </div>
      </div>

      <div>
        <h4 className="text-sm font-medium text-gray-400 mb-3">Threat Statistics</h4>
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-arbitium-darker/50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-red-400 mb-1">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-lg font-bold">{(stats?.total_indicators || 0).toLocaleString()}</span>
            </div>
            <div className="text-xs text-gray-400">Total Indicators</div>
          </div>
          <div className="bg-arbitium-darker/50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-orange-400 mb-1">
              <Hash className="w-4 h-4" />
              <span className="text-lg font-bold">{(stats?.malware_families || 0).toLocaleString()}</span>
            </div>
            <div className="text-xs text-gray-400">Malware Families</div>
          </div>
          <div className="bg-arbitium-darker/50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-purple-400 mb-1">
              <Globe className="w-4 h-4" />
              <span className="text-lg font-bold">{(stats?.threat_actors || 0).toLocaleString()}</span>
            </div>
            <div className="text-xs text-gray-400">Threat Actors</div>
          </div>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-arbitium-primary/10">
        <p className="text-xs text-gray-500">
          Last updated: {timestamp ? new Date(timestamp).toLocaleTimeString() : 'Unknown'}
        </p>
      </div>
    </div>
  );
};

export default ThreatIntelPanel;
